import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FormInputLabel } from 'blaze-form';
import { Alert, Row, Col, Button, FormGroup, FormControl } from 'react-bootstrap';
import ApiService from '../../services/ApiService';
import FormInputCalendarItem from '../form/control/FormInputCalendarItem';
import { modal } from '../modal/ModalEmitter';

const PostDealConfirmation = ({
  callback,
  deal,
  initialPostingDate,
  notready,
  postingBranch,
  postingData,
  postingSource,
  postingAddendum,
  ready,
  sources,
}) => {
  const today = moment().format('MM-DD-YYYY');
  const user = useSelector(state => state.app.user);

  const [loading, setLoading] = useState(false);
  const [postingDate, setPostingDate] = useState(initialPostingDate || today);
  const [errors, setErrors] = useState({});
  const [posted, setPosted] = useState({});

  const handlePostingDateChange = (fieldName, value) => {
    setPostingDate(value);
  };

  const handlePostDeal = () => {
    setLoading(true);

    const sourcesToPost = {};
    Object.keys(sources).forEach(sourceNumber => {
      if (!posted[sourceNumber]) {
        sourcesToPost[sourceNumber] = sources[sourceNumber];
      }
    });

    return ApiService.post(`crm/deal/${deal.id}/post`, {
      postingDate,
      postingSource,
      postingData,
      postingBranch,
      postingAddendum,
      sources: sourcesToPost,
    })
      .then(response => {
        const sourcesErrored = response.data;
        setErrors(sourcesErrored);

        const sourcesPosted = {};
        Object.keys(sources).forEach(sourceNumber => {
          if (sourcesErrored[sourceNumber]) {
            sourcesPosted[sourceNumber] = false;
          } else {
            sourcesPosted[sourceNumber] = true;
          }
        });

        if (Object.keys(sources).every(source => sourcesPosted[source] === true)) {
          modal.close();
        }

        setPosted(sourcesPosted);
        if (callback) {
          callback();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Check for balance
  const balances = {};
  Object.keys(sources).forEach(source => {
    balances[source] = 0;
    const entries = sources[source] || [];
    for (const entry of entries) {
      balances[source] += Math.round(entry[6] * 100);
    }
    balances[source] /= 100;
  });

  const allPosted = Object.keys(sources).every(source => posted[source] === true);

  const renderBranchSelection = () => {
    return (
      <FormGroup className="form-select is-readonly">
        <FormInputLabel>Branch</FormInputLabel>
        <FormControl as="select" readOnly disabled value={postingBranch}>
          {user.branches.map(branch => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </FormControl>
      </FormGroup>
    );
  };

  return (
    <div>
      {notready.length !== 0 && (
        <Alert className="p-2 mt-2" variant="danger">
          <i className="fas fa-exclamation-circle mr-1" /> {notready.length} entries are either unbalanced or no mapping
          is selected
        </Alert>
      )}
      <Row className="form-edit-container flex-row">
        <Col md={6} className="control-wrapper">
          <FormInputCalendarItem
            controlId="posting-date"
            field={{ label: 'Posting Date', inputProps: { showToday: true } }}
            value={postingDate}
            onChange={handlePostingDateChange}
          />
        </Col>
        <Col>{renderBranchSelection()}</Col>
      </Row>
      <div className="p-2 h6 mt-3 text-center">Multi-Unit ({ready.length}) Deal</div>
      {Object.keys(sources).map(source => {
        return (
          <div key={source}>
            <div className="source-line">
              <span className="flex-grow-1">
                {!!errors[source] && <i className="fas fa-exclamation-circle mr-1" style={{ color: 'var(--red-5)' }} />}
                {!!posted[source] && <i className="fas fa-check-circle mr-1" style={{ color: 'var(--green-5)' }} />}
                Source {source}
              </span>
              <span>{sources[source].length} entries</span>
              {balances[source] !== 0 && <span>({balances[source]})</span>}
            </div>
            {!!errors[source] && (
              <Alert className="p-2 mt-2" variant="danger">
                Source {source} failed to post: {errors[source]}
              </Alert>
            )}
          </div>
        );
      })}
      <div className="text-center mt-3 p-3">
        {allPosted === false ? (
          <Button onClick={handlePostDeal} disabled={loading}>
            {loading === true ? (
              <>
                <i className="fad fa-spinner-third fa-spin" /> Posting
              </>
            ) : (
              <>Post Deal</>
            )}
          </Button>
        ) : (
          <Button onClick={() => modal.close()} variant="secondary">
            Done
          </Button>
        )}
      </div>
    </div>
  );
};

export default PostDealConfirmation;
